import React, { useMemo } from "react";
import { SegmentRules, SegmentCondition } from "../types/segment";
import { CustomFieldDefinition } from "../types/customFields";
import { Trash2 } from "lucide-react";

interface FieldOption {
  value: string;
  label: string;
  type: string;
  choices?: string[];
}

interface SegmentRuleBuilderProps {
  rules: SegmentRules;
  onChange: (rules: SegmentRules) => void;
  onPreview: () => void;
  previewCount: number | null;
  previewLoading: boolean;
  customFieldDefinitions?: CustomFieldDefinition[];
}

const STATIC_FIELD_OPTIONS: FieldOption[] = [
  { value: "status", label: "Status", type: "string" },
  { value: "open_rate", label: "Open Rate (%)", type: "number" },
  { value: "click_rate", label: "Click Rate (%)", type: "number" },
  { value: "number_of_deliveries", label: "Deliveries", type: "number" },
  { value: "number_of_opens", label: "Opens", type: "number" },
  { value: "number_of_clicks", label: "Clicks", type: "number" },
  { value: "unique_opens", label: "Unique Opens", type: "number" },
  { value: "unique_clicks", label: "Unique Clicks", type: "number" },
  { value: "verified_clicks", label: "Verified Clicks", type: "number" },
  { value: "unique_verified_clicks", label: "Unique Verified Clicks", type: "number" },
  { value: "verified_click_rate", label: "Verified Click Rate (%)", type: "number" },
  { value: "days_since_last_open", label: "Days Since Last Open", type: "number" },
  { value: "days_since_last_click", label: "Days Since Last Click", type: "number" },
  { value: "days_since_last_engagement", label: "Days Since Last Engagement", type: "number" },
];

const RATE_FIELDS = ["open_rate", "click_rate", "verified_click_rate"];

const NUMERIC_OPERATORS = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "not equals" },
  { value: "greater_than", label: "greater than" },
  { value: "less_than", label: "less than" },
  { value: "greater_than_or_equal", label: ">= " },
  { value: "less_than_or_equal", label: "<=" },
];

const STRING_OPERATORS = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "not equals" },
];

const BOOLEAN_OPERATORS = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "not equals" },
];

const STATUS_VALUES = ["Active", "Inactive", "Unsubscribed", "Bounced"];

const SegmentRuleBuilder: React.FC<SegmentRuleBuilderProps> = ({
  rules,
  onChange,
  onPreview,
  previewCount,
  previewLoading,
  customFieldDefinitions = [],
}) => {
  const fieldOptions: FieldOption[] = useMemo(() => {
    const customOptions: FieldOption[] = customFieldDefinitions.map((def) => ({
      value: `cf_${def.id}`,
      label: def.name,
      type: def.field_type,
      choices: def.options?.choices,
    }));
    return [...STATIC_FIELD_OPTIONS, ...customOptions];
  }, [customFieldDefinitions]);

  const getFieldOption = (field: string): FieldOption | undefined => {
    return fieldOptions.find((f) => f.value === field);
  };

  const getFieldType = (field: string) => {
    return getFieldOption(field)?.type || "number";
  };

  const getOperators = (field: string) => {
    const type = getFieldType(field);
    if (type === "boolean") return BOOLEAN_OPERATORS;
    if (type === "string" || type === "text" || type === "select") return STRING_OPERATORS;
    return NUMERIC_OPERATORS;
  };

  const getDefaultValue = (field: string): string | number | boolean => {
    const type = getFieldType(field);
    if (field === "status") return "Active";
    if (type === "boolean") return true;
    if (type === "text" || type === "select") return "";
    if (type === "date") return "";
    return 0;
  };

  const updateCondition = (index: number, updates: Partial<SegmentCondition>) => {
    const newConditions = [...rules.conditions];
    newConditions[index] = { ...newConditions[index], ...updates } as SegmentCondition;

    if (updates.field) {
      newConditions[index].operator = "equals";
      newConditions[index].value = getDefaultValue(updates.field);
    }

    onChange({ ...rules, conditions: newConditions });
  };

  const addCondition = () => {
    onChange({
      ...rules,
      conditions: [
        ...rules.conditions,
        { field: "status", operator: "equals", value: "Active" },
      ],
    });
  };

  const removeCondition = (index: number) => {
    const newConditions = rules.conditions.filter((_, i) => i !== index);
    onChange({ ...rules, conditions: newConditions });
  };

  const renderValueInput = (condition: SegmentCondition, index: number) => {
    const fieldType = getFieldType(condition.field);
    const fieldOpt = getFieldOption(condition.field);

    const inputStyle = {
      padding: "6px 10px",
      border: "1px solid #d1d5db",
      borderRadius: "4px",
      fontSize: "14px",
      minWidth: "120px",
    };

    // Status field (built-in)
    if (condition.field === "status") {
      return (
        <select
          value={condition.value as string}
          onChange={(e) => updateCondition(index, { value: e.target.value })}
          style={inputStyle}
        >
          {STATUS_VALUES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      );
    }

    // Rate fields with percentage display
    if (RATE_FIELDS.includes(condition.field)) {
      return (
        <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
          <input
            type="number"
            step="1"
            min="0"
            max="100"
            value={Math.round((condition.value as number) * 100)}
            onChange={(e) => updateCondition(index, { value: (parseFloat(e.target.value) || 0) / 100 })}
            style={{ ...inputStyle, paddingRight: "24px", width: "100px" }}
          />
          <span style={{ position: "absolute", right: "8px", color: "#9ca3af", fontSize: "14px", pointerEvents: "none" }}>%</span>
        </div>
      );
    }

    // Select custom field
    if (fieldType === "select" && fieldOpt?.choices) {
      return (
        <select
          value={condition.value as string}
          onChange={(e) => updateCondition(index, { value: e.target.value })}
          style={inputStyle}
        >
          <option value="">Select...</option>
          {fieldOpt.choices.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      );
    }

    // Boolean custom field
    if (fieldType === "boolean") {
      return (
        <select
          value={String(condition.value)}
          onChange={(e) => updateCondition(index, { value: e.target.value === "true" })}
          style={inputStyle}
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    // Date custom field
    if (fieldType === "date") {
      return (
        <input
          type="date"
          value={condition.value as string}
          onChange={(e) => updateCondition(index, { value: e.target.value })}
          style={inputStyle}
        />
      );
    }

    // Text custom field
    if (fieldType === "text") {
      return (
        <input
          type="text"
          value={condition.value as string}
          onChange={(e) => updateCondition(index, { value: e.target.value })}
          placeholder="Enter value"
          style={{ ...inputStyle, width: "150px" }}
        />
      );
    }

    // Default: number input
    return (
      <input
        type="number"
        step="1"
        value={condition.value as number}
        onChange={(e) => updateCondition(index, { value: parseFloat(e.target.value) || 0 })}
        style={{ ...inputStyle, width: "100px" }}
      />
    );
  };

  return (
    <div>
      <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "14px", color: "#374151" }}>Match</span>
        <select
          value={rules.match}
          onChange={(e) => onChange({ ...rules, match: e.target.value as "all" | "any" })}
          style={{
            padding: "6px 10px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "14px",
          }}
        >
          <option value="all">All conditions (AND)</option>
          <option value="any">Any condition (OR)</option>
        </select>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {rules.conditions.map((condition, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              backgroundColor: "#f9fafb",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
            }}
          >
            <select
              value={condition.field}
              onChange={(e) => updateCondition(index, { field: e.target.value })}
              style={{
                padding: "6px 10px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                fontSize: "14px",
                minWidth: "140px",
              }}
            >
              {fieldOptions.length > STATIC_FIELD_OPTIONS.length && (
                <optgroup label="Engagement">
                  {STATIC_FIELD_OPTIONS.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </optgroup>
              )}
              {fieldOptions.length > STATIC_FIELD_OPTIONS.length && (
                <optgroup label="Custom Fields">
                  {fieldOptions.filter((f) => f.value.startsWith("cf_")).map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </optgroup>
              )}
              {fieldOptions.length === STATIC_FIELD_OPTIONS.length &&
                fieldOptions.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))
              }
            </select>

            <select
              value={condition.operator}
              onChange={(e) => updateCondition(index, { operator: e.target.value as SegmentCondition["operator"] })}
              style={{
                padding: "6px 10px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                fontSize: "14px",
                minWidth: "120px",
              }}
            >
              {getOperators(condition.field).map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>

            {renderValueInput(condition, index)}

            {(
              <button
                onClick={() => removeCondition(index)}
                style={{
                  padding: "6px",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  color: "#9ca3af",
                }}
                title="Remove condition"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: "12px" }}>
        <button
          onClick={addCondition}
          style={{
            padding: "6px 12px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            backgroundColor: "#fff",
            cursor: "pointer",
            fontSize: "13px",
          }}
        >
          + Add Condition
        </button>

        <button
          onClick={onPreview}
          disabled={previewLoading || rules.conditions.length === 0}
          style={{
            padding: "6px 12px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            backgroundColor: "#f3f4f6",
            cursor: previewLoading ? "not-allowed" : "pointer",
            fontSize: "13px",
          }}
        >
          {previewLoading ? "Counting..." : "Preview"}
        </button>

        {previewCount !== null && (
          <span style={{ fontSize: "14px", color: "#374151", fontWeight: 500 }}>
            {previewCount.toLocaleString()} subscriber{previewCount !== 1 ? "s" : ""} match
          </span>
        )}
      </div>
    </div>
  );
};

export default SegmentRuleBuilder;
